import { ActionModal } from '@kcftech/design-system';
import {
    AssetCategoryParameterType,
    IAssetCategoryParameterChild,
    IAssetCategoryResponse,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { AssetParameterFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterFormContent';
import { buildCategoryParameter } from 'pages/Settings/AssetType/parameter_form/shared';
import { AssetParameterFormData } from 'pages/Settings/AssetType/parameter_form/types';
import { useForm } from 'react-hook-form';
import { createAssetParameter } from 'utils/asset_classification';

export type AssetParameterCreationInfo = Readonly<{
    category: IAssetCategoryResponse;
    siblingParameters: IAssetCategoryParameterChild[];
}>;

export type CreateAssetParameterProps = AssetParameterCreationInfo &
    Readonly<{
        onClose: (saved: boolean) => void;
    }>;

export function CreateAssetParameterModal({
    category,
    siblingParameters,
    onClose,
}: CreateAssetParameterProps) {
    const { showToast } = useNotificationContext();

    const form = useForm<AssetParameterFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        defaultValues: {
            name: '',
            code: '',
            type: AssetCategoryParameterType.Text,
            defaultValue: '',
        },
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const { mutate: createParameter, isPending: isSaving } = useMutation({
        mutationFn: async (formData: AssetParameterFormData) =>
            createAssetParameter(category.Id, buildCategoryParameter(formData)),

        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully created asset category parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create asset category parameter',
            }),
    });

    const onSave = handleSubmit((formData) => createParameter(formData));

    return (
        <ActionModal
            aria-label="Create Asset Category Parameter"
            title="Create Asset Category Parameter"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isSaving)
            }
            onAction={onSave}
            onClose={() => onClose(false)}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <AssetParameterFormContent
                form={form}
                siblingParameters={siblingParameters}
                parentCategory={category}
            />
        </ActionModal>
    );
}
